/**
 * GraphNode — Individual node in the force-directed graph.
 *
 * Uses foreignObject to render HTML (icons via resolveIcon).
 */

import React from 'react';
import { resolveIcon } from '../common/IconResolver';
import { NODE_RADIUS, HEALTH_COLORS, HEALTH_BADGE_RADIUS } from './constants';
import type { SimNode } from './types';

interface Props {
  node: SimNode;
  highlighted: boolean;
  dimmed: boolean;
  onMouseEnter: (node: SimNode) => void;
  onMouseLeave: () => void;
  onClick: (node: SimNode) => void;
}

const GraphNode: React.FC<Props> = ({ node, highlighted, dimmed, onMouseEnter, onMouseLeave, onClick }) => {
  const opacity = dimmed ? 0.15 : 1;
  const strokeWidth = highlighted ? 3 : 2;
  const healthColor = HEALTH_COLORS[node.healthStatus] || HEALTH_COLORS.unknown;
  const r = NODE_RADIUS;

  return (
    <g
      transform={`translate(${node.x}, ${node.y})`}
      style={{ cursor: 'pointer', opacity, transition: 'opacity 0.2s' }}
      onMouseEnter={() => onMouseEnter(node)}
      onMouseLeave={onMouseLeave}
      onClick={() => onClick(node)}
    >
      {/* Background circle */}
      <circle
        r={r}
        fill={node.color}
        opacity={0.15}
        stroke={node.color}
        strokeWidth={strokeWidth}
      />

      {/* Icon via foreignObject */}
      <foreignObject x={-12} y={-12} width={24} height={24}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: node.color,
            width: '100%',
            height: '100%',
          }}
        >
          {resolveIcon(node.icon, 18)}
        </div>
      </foreignObject>

      {/* Label below circle */}
      <text
        y={r + 16}
        textAnchor="middle"
        fontSize="11"
        fill="var(--text-primary, #e2e8f0)"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {node.label}
      </text>

      {/* Health badge */}
      <circle
        cx={r * 0.7}
        cy={-r * 0.7}
        r={HEALTH_BADGE_RADIUS}
        fill={healthColor}
        stroke="var(--bg-primary, #0f172a)"
        strokeWidth={2}
      />
    </g>
  );
};

export default GraphNode;
